import React, { useEffect, useState } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import makeStyles from '@material-ui/styles/makeStyles';

import { loadMergeIntegration } from './actions/load-merge-integration';
import {
  mergeIntegrationForOrgIdSelector,
  mergeIntegrationsStateForOrgIdSelector,
} from './merge-integration-selectors';
import { loadAccountUsers } from '../action/user/load-account-users';
import { loadHrisImportLogs } from '../hris-import-log/actions/load-hris-import-logs';
import { hrisImportLogForMergeIntegrationIdSelector } from '../hris-import-log/hris-import-log-selectors';
import { loadHrisIntegrationResponse } from '../hris-integration-response/actions/load-hris-integration-response';
import { updateHrisIntegrationResponse } from '../hris-integration-response/actions/update-hris-integration-response';
import { hrisIntegrationResponseForOrgIdSelector } from '../hris-integration-response/hris-integration-response-selectors';
import { userSelector } from '../reducers/current-user-selectors';
import { orgsSelector } from '../reducers/org-selectors';
import { userForUsernameSelector } from '../reducers/user-selectors';

import { useApi } from '../api/use-api';
import { HrisImportLogsTable } from '../hris-import-log/hris-import-logs-table';
import { UiGreenSwitch } from '../ui/ui-green-switch';

import { MergeIntegrationSyncHealthIndicator } from './merge-integration-sync-health-indicator';

import { getSyncStatusMetadata } from './util/get-sync-status-metadata';
import { formatDateTime, formatRelativeDate } from '../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { sortByDate } from '../util/sort-functions';
import Message from '../widgets/legacy-message';
import { LoadingMessage } from '../widgets/loading-message';

const useStyles = makeStyles(() => ({
  mergeMetadata: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '32%',
    minWidth: '215px',
  },
}));

const mapStateToProps =
  ({ org }: Props) =>
  (state: any) => {
    const mergeIntegration = mergeIntegrationForOrgIdSelector(state, org.org_id);
    const integrationUser = mergeIntegration
      ? userForUsernameSelector(state, mergeIntegration.createdBy)
      : null;
    const hrisImportLogs = mergeIntegration
      ? hrisImportLogForMergeIntegrationIdSelector(state, mergeIntegration?.mergeIntegrationId)
      : null;
    const currentUser = userSelector(state);

    return {
      mergeIntegration,
      mergeIntegrationState: mergeIntegrationsStateForOrgIdSelector(state, org.org_id),
      hrisImportLogs,
      integrationUser,
      currentUser,
      hasMultipleOrgs: orgsSelector(state).length > 1,
      hrisIntegrationResponse: hrisIntegrationResponseForOrgIdSelector(state, org.org_id),
    };
  };

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>, { org }: Props) => ({
  actions: {
    loadMergeIntegration: () => dispatch(loadMergeIntegration(org.org_id)),
    loadHrisImportLogs: (mergeIntegrationId: number) =>
      dispatch(loadHrisImportLogs(mergeIntegrationId)),
    loadAccountUsers: () => dispatch(loadAccountUsers(org.accountId)),
    loadHrisIntegrationResponse: () => dispatch(loadHrisIntegrationResponse(org.org_id)),
    updateHrisIntegrationResponse: (update: HrisIntegrationResponseUpdateRequest) =>
      dispatch(updateHrisIntegrationResponse(update)),
  },
});

type Props = {
  org: Org;
};

export const MergeIntegrationDetails = ({ org }: Props) => {
  const {
    mergeIntegration,
    mergeIntegrationState,
    hrisImportLogs,
    integrationUser,
    currentUser: { isAdmin },
    hrisIntegrationResponse,
  } = useSelector(mapStateToProps({ org }), shallowEqual);
  const { actions } = mapDispatchToProps(useDispatch(), { org });
  const { mergeMetadata } = useStyles();

  const { data: accountDetails, errorMessage: detailsError } = useApi<MergeAccountDetails | {}>({
    url: `/merge/account-details?orgId=${org.org_id}`,
  });

  const {
    data: aggregatedSyncStatus,
    errorMessage: statusError,
    loading: statusLoading,
    refetch: refreshStatus,
  } = useApi<MergeAggregatedSyncStatus | {}>({
    url: `/merge/aggregated-sync-status?orgId=${org.org_id}`,
  });

  useEffect(() => {
    actions.loadMergeIntegration();
    actions.loadAccountUsers();
    actions.loadHrisIntegrationResponse();
    if (mergeIntegration?.mergeIntegrationId) {
      actions.loadHrisImportLogs(mergeIntegration?.mergeIntegrationId);
    }
  }, [actions, mergeIntegration?.mergeIntegrationId]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleExpandCollapse = () => setIsExpanded(!isExpanded);

  if (!mergeIntegrationState || mergeIntegrationState.loading) {
    return <LoadingMessage />;
  }

  const handleEnableMultiOrgSync = () => {
    actions.updateHrisIntegrationResponse({
      ...hrisIntegrationResponse,
      orgId: org.org_id,
      isTwoStepSetupEnabled: !hrisIntegrationResponse.isTwoStepSetupEnabled,
    });
  };

  if (!mergeIntegration) {
    return (
      <>
        <div className="typo-h6">Merge Integration</div>
        <div className="typo-subtitle">
          There is no Merge integration found for this account. If you&apos;d like to allow the user
          to set up a potentially risky HRIS integration (requires multi-org sync, or is for a
          system we haven&apos;t set up a bunch of times), click the switch below
        </div>
        <div className="u-flex">
          <UiGreenSwitch
            checked={hrisIntegrationResponse?.isTwoStepSetupEnabled}
            onChange={handleEnableMultiOrgSync}
          />
          Allow user to proceed with setup
        </div>
      </>
    );
  }

  const handleRefreshStatus = () => {
    refreshStatus();
  };
  const sortedHrisImportLogs = (hrisImportLogs || []).sort(sortByDate((log) => log.createdAt));

  const { createdAt } = mergeIntegration;
  const lastSyncDate = sortedHrisImportLogs?.length ? sortedHrisImportLogs[0].finishedAt : null;
  const userFullName = integrationUser
    ? `${integrationUser.firstName} ${integrationUser.lastName}`
    : 'N/A';
  const mergeAccountNumber = accountDetails && 'id' in accountDetails ? accountDetails.id : null;

  // The aggregatedSyncStatus that we fetch from Merge is the most up to date
  // and contains relevant information to users. However, admins are able to
  // view our local statuses that we set in the backend. When those statuses
  // are present, they should appear in the display for admins instead of the
  // status Merge has
  let syncStatusToUse: MergeSyncStatus | undefined =
    aggregatedSyncStatus && 'status' in aggregatedSyncStatus
      ? aggregatedSyncStatus.status
      : undefined;
  if (isAdmin && mergeIntegration?.syncStatus?.startsWith('IMPORT')) {
    syncStatusToUse = mergeIntegration.syncStatus;
  }

  const { syncStatusTitle, syncStatusTooltip } = getSyncStatusMetadata(syncStatusToUse) ?? {};

  const hasError = detailsError || statusError;
  const expandCollapseClassNames = 'u-verticalAlign-middle';

  return (
    <>
      {isAdmin && (
        <>
          <div className="typo-h5 u-margin-bottom u-flex-center">
            {org.name}&apos;s Integration
            <a
              className={`${mergeAccountNumber ? 'typo-link' : 'typo-disabled'} u-margin-left-sm`}
              target="_blank"
              rel="noopener noreferrer"
              href={`https://app.merge.dev/linked-accounts/account/${mergeAccountNumber}/overview`}
              style={{ width: '24px', height: '24px' }}
              aria-label="Open"
            >
              <MaterialDesignIcon iconName={ICON_NAMES.OPEN_IN_NEW} />
            </a>
          </div>
          {hasError && (
            <div>
              <Message
                variant="error"
                className="u-margin-bottom"
                message={detailsError ?? statusError}
              />
            </div>
          )}
        </>
      )}
      <div className="u-border u-margin-bottom">
        <div className="typo-body u-padding u-display-flex u-justifyContent-spaceBetween">
          <MergeIntegrationSyncHealthIndicator
            mergeOriginOrgId={mergeIntegration.orgId}
            orgId={org.org_id}
            accountId={org.accountId}
            showSyncHealthDescription
          >
            <div>
              <span className="typo-bold">Last sync: </span>
              <span className="typo-subtitle">
                {lastSyncDate ? formatRelativeDate(lastSyncDate) : 'N/A'}
              </span>
            </div>
          </MergeIntegrationSyncHealthIndicator>
          <div
            onClick={handleExpandCollapse}
            role="presentation"
            className="u-cursor-pointer u-display-flex"
          >
            <span className="typo-bold">Details </span>
            {isExpanded ? (
              <MaterialDesignIcon
                iconName={ICON_NAMES.EXPAND_LESS}
                className={expandCollapseClassNames}
                size="sm"
              />
            ) : (
              <MaterialDesignIcon
                iconName={ICON_NAMES.EXPAND_MORE}
                className={expandCollapseClassNames}
                size="sm"
              />
            )}
          </div>
        </div>
        <Collapse in={isExpanded}>
          <div className="typo-body u-padding u-display-flex u-flexWrap-wrap u-row-gap-10px">
            <div className={mergeMetadata}>
              <div className="typo-bold">Created on</div>
              <div className="typo-subtitle u-margin-top-sm">{formatDateTime(createdAt)}</div>
            </div>
            <div className={mergeMetadata}>
              <div className="typo-bold">Created by</div>
              <div className="typo-subtitle u-margin-top-sm">
                {userFullName} {integrationUser?.email ? `(${integrationUser.email})` : ''}
              </div>
            </div>
            {isAdmin ? (
              <div className={mergeMetadata}>
                <div className="typo-bold u-display-flex u-alignItems-flexStart">
                  Sync Status
                  <div>
                    <IconButton
                      edge="start"
                      size="small"
                      onClick={handleRefreshStatus}
                      title="Refresh"
                      style={{ marginTop: '-5px' }}
                    >
                      {statusLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <MaterialDesignIcon iconName={ICON_NAMES.REFRESH} size="sm" />
                      )}
                    </IconButton>
                  </div>
                </div>
                <div className="typo-subtitle u-margin-top-sm u-flex-center u-gap-5px">
                  {statusLoading ? 'Loading...' : syncStatusTitle}
                  {syncStatusTooltip && (
                    <Tooltip title={syncStatusTooltip}>
                      <MaterialDesignIcon iconName={ICON_NAMES.INFO} size="sm" />
                    </Tooltip>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </Collapse>
      </div>
      <div className="typo-h6">Recent Imports</div>
      <HrisImportLogsTable
        mergeIntegrationId={mergeIntegration.mergeIntegrationId}
        orgIdFilter={org.org_id !== mergeIntegration.orgId ? org.org_id : null}
      />
    </>
  );
};
